// src/index.js
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "./lang/i18n";
// Roboto: loaded from public/fonts/roboto/roboto.css (fully offline). Run npm run download-fonts once if missing.
// Noto Sans Arabic for Dari & Pashto (self-hosted via @fontsource)
import "@fontsource/noto-sans-arabic/400.css";
import "@fontsource/noto-sans-arabic/500.css";
import "@fontsource/noto-sans-arabic/600.css";
import "@fontsource/noto-sans-arabic/700.css";
import "./api/axiosSetup"; // Centralized API error handling - must load before any API calls
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { RouterProvider } from "react-router-dom";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { SnackbarProvider } from "notistack";
import { AuthProvider, useAuth } from "./context";
import { getAccess } from "./permissions/Permissions";
import { createAppRouter } from "./Routes/AppRoutes";
import { NewRecordsProvider } from "./context/NewRecordsProvider";
import AppThemeProvider from "./Components/theme/AppThemeProvider";
import useFetchObjects from "./api/useFetchObjects";

const queryClient = new QueryClient({
  defaultOptions: {},
});

const AppWithRouter = () => {
  const auth = useAuth();
  const token = auth?.user?.token;
  const user = auth?.user;
  const userId = user?.user?.id;
  const cachedPermissions = user?.user?.permissions || {};

  const { data } = useFetchObjects(
    ["permissions-users", userId],
    userId ? `users/${userId}/` : "",
    token,
    { enabled: !!userId && !!token }
  );

  const transformPermissions = (data) => {
    if (!data) return {};

    return {
      reception: data.reception || false,
      pharmacy: data.pharmacy || false,
      doctor: data.doctor || false,
      nurse: data.nurse || false,
      x_ray: data.x_ray || false,
      ultrasounds: data.ultrasounds || false,
      laboratory: data.laboratory || false,
      endoscopy: data.endoscopy || false,
      ct_scan: data.ct_scan || false,
      m_roy: data.m_roy || false,
      echo: data.echo || false,
      finance: data.finance || false,
      financial_manager: data.financial_manager || false,
      hr: data.hr || false,
      academic: data.academic || false,
      dental: data.dental || false,
      call_center: data.call_center || false,
      limited_data_view: data.limited_data_view || false,
      is_manager_in_call_center: data.is_manager_in_call_center || false,
      beauty: data.beauty || false,
      htPatient: data.htPatient || false,
      operations: data.operations || false,
      emergency: data.emergency || false,
      bedManagement: data.bedManagement || false,
      ipd: data.ipd || false,
      icu: data.icu || false,
      reports: data.reports || false,
      night_shift: data.night_shift || false,
      night_shift_manage: data.night_shift_manage || false,
      reception_dashboard: data.reception_dashboard || false,
      reception_new_patient: data.reception_new_patient || false,
      reception_patient_list: data.reception_patient_list || false,
      reception_patient_referral: data.reception_patient_referral || false,
      reception_opd: data.reception_opd || false,
      reception_ticketing: data.reception_ticketing || false,
      reception_payment_collection: data.reception_payment_collection || false,
      reception_medicine_fees: data.reception_medicine_fees || false,
      reception_dental_fees: data.reception_dental_fees || false,
      reception_emergency_fees: data.reception_emergency_fees || false,
      reception_ipd_fees: data.reception_ipd_fees || false,
      reception_icu_fees: data.reception_icu_fees || false,
      reception_return_medicine: data.reception_return_medicine || false,
    };
  };

  const permissions = transformPermissions(data || cachedPermissions);

  const userPermissions = getAccess(permissions, user?.user?.isAdmin);
  const router = createAppRouter(userPermissions);

  return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <AppThemeProvider>
    <AuthProvider>
      <NewRecordsProvider>
        <ToastContainer />
        <QueryClientProvider client={queryClient}>
          <SnackbarProvider>
            <React.StrictMode>
              <AppWithRouter />
            </React.StrictMode>
          </SnackbarProvider>
        </QueryClientProvider>
      </NewRecordsProvider>
    </AuthProvider>
  </AppThemeProvider>
);
